import { v4 as uuidv4 } from "uuid";
import { photoshop } from "../globals";
import { useListenFromRenderer } from "../message";
import { postToRenderer } from "../message/utils";
import { fetchInferenceJob } from "./shared/fetch-inference-job";
import { getTargetEditorImage } from "./shared/target-editor-image";

const callCommunityToolGenerate = async (
	community_tool_document_id: string,
	tool_name: string,
	messageId: string,
	jwt: string,
	batch_size: number,
	control_values?: Record<string, string | number | boolean>,
	user_image_map?: Record<string, string[]>,
	use_editor_image = true,
	background_remover?: boolean,
	resolution?: string,
) => {
	const { success } = await getTargetEditorImage({
		commandName: `Community Tool - ${tool_name}`,
		resultLayerName: tool_name,
		placeAsSmartObject: true,
		preventSmartObjectTrim: !!background_remover,
		maxSide: undefined,
		processImage: async ({ executionContext, imageBase64, selBounds }) => {
			const data: Record<string, unknown> = {
				community_tool_document_id,
				input_images: use_editor_image ? [imageBase64] : [],
				jwt,
				batch_size,
				from_editor: use_editor_image,
			};
			if (control_values) data.control_values = control_values;
			if (user_image_map) data.user_image_map = user_image_map;
			if (background_remover !== undefined) data.background_remover = background_remover;
			if (resolution) data.resolution = resolution;
			// 선택영역이 있으면 선택영역 크기, 없으면 doc 전체 크기
			data.editor_width = selBounds.width;
			data.editor_height = selBounds.height;

			return fetchCommunityToolGenerate(
				data as Parameters<typeof fetchCommunityToolGenerate>[0],
				executionContext,
				messageId,
			);
		},
	});

	if (success) {
		const doc = photoshop.app.activeDocument;
		if (doc) {
			postToRenderer([
				{
					key: "documentInfoChanged",
					id: uuidv4(),
					values: {
						width: doc.width,
						height: doc.height,
					},
				},
			]);
		}
	}

	return success;
};

export async function fetchCommunityToolGenerate(
	params: {
		community_tool_document_id: string;
		input_images: string[];
		batch_size: number;
		from_editor: boolean;
		control_values?: Record<string, string | number | boolean>;
		user_image_map?: Record<string, string[]>;
		editor_width?: number;
		editor_height?: number;
		background_remover?: boolean;
		resolution?: string;
	},
	executionContext: any,
	messageId: string,
): Promise<string | string[] | false> {
	return fetchInferenceJob({
		params,
		executionContext,
		messageId,
		apiPath: "/infer/community-tool",
		statusEndpoint: (apiUrl, jobId) =>
			`${apiUrl.replace("/infer/community-tool", "")}/status/${jobId}`,
		resultEndpoint: (apiUrl, jobId) =>
			`${apiUrl.replace("/infer/community-tool", "")}/result/${jobId}`,
		cancelEndpoint: (apiUrl, jobId) => ({
			url: `${apiUrl.replace("/infer/community-tool", "")}/cancel/${jobId}`,
			method: "POST",
		}),
		extractResult: (data) => data.image_base64_list ?? [data.image_base64],
		useGatewayUrl: true,
	});
}

export function useListenCommunityToolGenerateMessage() {
	useListenFromRenderer([
		{
			key: "communityToolGenerate",
			onMessage: async (message) => {
				const success = await callCommunityToolGenerate(
					message.values.community_tool_document_id,
					message.values.tool_name,
					message.id,
					message.values.jwt,
					message.values.batch_size,
					message.values.control_values,
					message.values.user_image_map,
					message.values.use_editor_image ?? true,
					message.values.background_remover,
					message.values.resolution,
				);
				postToRenderer([
					{
						key: "finish",
						id: uuidv4(),
						values: {
							id: message.id,
							status: success ? "success" : "error",
						},
					},
				]);
			},
		},
	]);
}
